import { useSeo } from "../lib/seo";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "../lib/api";
import { trackEvent } from "../lib/analytics";
import { getDeviceId, local } from "../lib/storage";
import { useAuth } from "../context/AuthContext";
import { PageShell, Panel } from "../components/PageShell";
import { PopButton } from "../components/PopButton";
import { TIMER_CHOICES } from "../components/group/Countdown";
import { SoundStylePicker } from "../components/SoundStylePicker";
import type { SoundStyle } from "../lib/sound";

const MODES = [
  { id: "normal", name: "Quick vote", desc: "One round. Most votes wins. Tie? DITHER picks from the tied options." },
  { id: "best_of_3", name: "Best of 3", desc: "Up to 3 rounds. First option to win 2 rounds takes it. Tied rounds go to DITHER." },
] as const;

export default function GroupCreate() {
  useSeo({ title: "Group Decision Maker – Live Poll & Vote with Friends | DITHER", description: "Create a group poll in seconds, share the link and vote by name — live results, tiebreaker and Best of 3. No sign-up needed.", path: "/group/new" });
  const navigate = useNavigate();
  const { user } = useAuth();
  const [title, setTitle] = useState("");
  const [name, setName] = useState(user ? user.name : "");
  const [options, setOptions] = useState<string[]>(["", ""]);
  const [mode, setMode] = useState<"normal" | "best_of_3">("normal");
  const [busy, setBusy] = useState(false);
  const [timer, setTimer] = useState(0);
  const [soundStyle, setSoundStyle] = useState<SoundStyle>("gameshow");

  const filled = options.map((o) => o.trim()).filter(Boolean);
  const dupes = filled.length !== new Set(filled.map((o) => o.toLowerCase())).size;
  const valid = title.trim() && name.trim() && filled.length >= 2 && !dupes;

  const submit = async () => {
    if (!valid) return;
    setBusy(true);
    try {
      const { data } = await api.post("/groups", { title, host_name: name, options: filled, mode, time_limit_sec: timer, sound_style: soundStyle });
      const id = data.group.id;
      local.setHostToken(id, data.host_token);
      trackEvent("group_created", { kind: mode });
      await api.post(`/groups/${id}/join`, { name, device_id: getDeviceId() });
      navigate(`/g/${id}`);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <PageShell title="Group decision" subtitle="Make a poll, share the link, let everyone vote by name." testId="group-create-page">
      <Panel className="space-y-6">
        <label className="block text-sm font-extrabold uppercase">What are we deciding?
          <input className="field mt-1" maxLength={100} placeholder="Where are we eating tonight?" value={title} onChange={(e) => setTitle(e.target.value)} data-testid="group-title-input" />
        </label>
        <label className="block text-sm font-extrabold uppercase">Your name
          <input className="field mt-1" maxLength={30} placeholder="Sam" value={name} onChange={(e) => setName(e.target.value)} data-testid="group-host-name-input" />
        </label>
        <div>
          <p className="mb-2 text-sm font-extrabold uppercase">Options <span className="font-semibold normal-case text-zinc-500">({filled.length}/12)</span></p>
          <div className="space-y-2">
            {options.map((o, i) => (
              <div key={i} className="flex gap-2">
                <input className="field" maxLength={80} placeholder={`Option ${i + 1}`} value={o} data-testid={`group-option-input-${i}`}
                  onChange={(e) => setOptions(options.map((x, j) => (j === i ? e.target.value : x)))} />
                {options.length > 2 && (
                  <button aria-label="Remove option" className="rounded-xl border-2 border-ink px-3 hover:bg-pop-pink hover:text-white" onClick={() => setOptions(options.filter((_, j) => j !== i))} data-testid={`group-option-remove-${i}`}>
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
            ))}
          </div>
          {dupes && <p className="mt-2 text-sm font-bold text-[#E11D48]" data-testid="group-duplicate-warning">Two options are the same — make them different.</p>}
          {options.length < 12 && (
            <button className="mt-3 inline-flex items-center gap-1 text-sm font-extrabold uppercase hover:underline" onClick={() => setOptions([...options, ""])} data-testid="group-add-option-button">
              <Plus className="h-4 w-4" strokeWidth={3} /> Add option
            </button>
          )}
        </div>
        <div>
          <p className="mb-2 text-sm font-extrabold uppercase">Mode</p>
          <div className="grid gap-3 sm:grid-cols-2">
            {MODES.map((m) => (
              <button key={m.id} onClick={() => setMode(m.id)} data-testid={`group-mode-${m.id}`} data-active={mode === m.id}
                className="rounded-2xl border-[3px] border-ink bg-white p-4 text-left transition-colors data-[active=true]:bg-pop-purple data-[active=true]:text-white">
                <span className="block font-display text-lg font-bold uppercase">{m.name}</span>
                <span className="text-sm font-medium opacity-80">{m.desc}</span>
              </button>
            ))}
          </div>
        </div>
        <div>
          <p className="mb-2 text-sm font-extrabold uppercase">Time limit <span className="font-semibold normal-case text-zinc-500">(voting closes automatically{mode === "best_of_3" ? ", per round" : ""})</span></p>
          <div className="flex flex-wrap gap-2">
            {TIMER_CHOICES.map((t) => (
              <button key={t.s} className="chip" data-active={timer === t.s} onClick={() => setTimer(t.s)} data-testid={`group-timer-${t.s}`}>{t.label}</button>
            ))}
          </div>
          {!!timer && <p className="mt-2 text-xs font-semibold text-zinc-500">If nobody votes in time, DITHER picks at random.</p>}
        </div>
        <div>
          <p className="mb-2 text-sm font-extrabold uppercase">Reveal sound style <span className="font-semibold normal-case text-zinc-500">(tap to preview)</span></p>
          <SoundStylePicker value={soundStyle} onChange={setSoundStyle} place="create" />
        </div>
        <PopButton tone="purple" size="lg" className="w-full" disabled={!valid || busy} onClick={submit} data-testid="group-create-submit-button">
          {busy ? "Creating…" : "Create & get link"}
        </PopButton>
        <p className="text-center text-xs font-semibold text-zinc-500">Guest links expire after 7 days (30 days when signed in).</p>
      </Panel>
    </PageShell>
  );
}
